import useScrollReveal from '@/hooks/useScrollReveal';
import useOpenStatus, { DayKey } from '@/hooks/useOpenStatus';

const openingHours: { day: string; hours: string; key: DayKey; highlight?: boolean }[] = [
  { day: 'Ponedeljek – Četrtek', hours: '06:30 – 23:00', key: 'weekday' },
  { day: 'Petek – Sobota', hours: '07:00 – 03:00', key: 'weekend', highlight: true },
  { day: 'Nedelja', hours: '09:00 – 22:00', key: 'sunday' },
];

export default function HoursSection() {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.15 });
  const { isOpen, dayKey } = useOpenStatus();

  return (
    <section id="delovni-cas" className="relative py-16 md:py-24 px-4 md:px-6 bg-background-50 scroll-mt-16 md:scroll-mt-20" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
          {/* Hours Side */}
          <div
            className={`w-full lg:w-5/12 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-primary-500/70" aria-hidden="true"></span>
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-400">
                Obiščite nas
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight">
              Delovni čas & Lokacija
            </h2>
            <p className="mt-4 text-foreground-300 text-sm md:text-base leading-relaxed">
              Najdete nas na naslovu <strong className="text-foreground-100">Zamušani 2, 2272 Gorišnica</strong>, tik ob glavni cesti. Parkirišče je na voljo pred lokalom.
            </p>

            {/* Hours Table */}
            <div className="mt-7 bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 bg-background-100">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center">
                      <i className="ri-time-line text-primary-600 text-lg"></i>
                    </div>
                    <span className="text-sm font-semibold text-foreground-100">Odpiralni čas</span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full ${
                      isOpen
                        ? 'bg-accent-500/15 text-accent-400'
                        : 'bg-foreground-950/10 text-foreground-400'
                    }`}
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      {isOpen && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60"></span>
                      )}
                      <span
                        className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                          isOpen ? 'bg-accent-400' : 'bg-foreground-600'
                        }`}
                      ></span>
                    </span>
                    {isOpen ? 'Odprto zdaj' : 'Zaprto'}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-background-200/40">
                {openingHours.map((item, idx) => {
                  const isToday = item.key === dayKey;
                  return (
                    <div
                      key={idx}
                      className={`flex items-center justify-between px-5 py-3.5 transition-colors ${
                        isToday
                          ? 'bg-primary-900/60 border-l-2 border-primary-500'
                          : item.highlight
                          ? 'bg-accent-900/40'
                          : ''
                      }`}
                    >
                      <span className={`text-sm flex items-center gap-2 ${isToday ? 'text-foreground-100 font-semibold' : 'text-foreground-300'}`}>
                        {item.day}
                        {isToday && (
                          <span className="inline-flex items-center text-[10px] bg-primary-500 text-foreground-50 px-1.5 py-0.5 rounded-full font-medium">
                            Danes
                          </span>
                        )}
                      </span>
                      <span className={`text-sm font-semibold whitespace-nowrap ${
                        isToday ? 'text-primary-300' : item.highlight ? 'text-accent-300' : 'text-foreground-100'
                      }`}>
                        {item.hours}
                        {item.highlight && !isToday && (
                          <span className="ml-2 inline-flex items-center text-[10px] bg-accent-500 text-foreground-50 px-1.5 py-0.5 rounded-full">
                            Vikend
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <a
                href="tel:051240383"
                className="whitespace-nowrap flex-1 text-center px-5 py-3 rounded-md bg-primary-500 text-foreground-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-phone-line mr-1.5"></i>
                Pokliči zdaj
              </a>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Zamušani+2,+2272+Gorišnica"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap flex-1 text-center px-5 py-3 rounded-md border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-navigation-line mr-1.5"></i>
                Navodila za pot
              </a>
            </div>
          </div>

          {/* Map Side */}
          <div
            className={`w-full lg:w-7/12 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="w-full h-[340px] md:h-[460px] rounded-lg overflow-hidden border border-background-200/60">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2743.5!2d16.0203!3d46.4223!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDbCsDI1JzIwLjMiTiAxNsKwMDEnMTMuMSJF!5e0!3m2!1ssl!2ssi!4v1690000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Guma Bar Zamušani - Lokacija na zemljevidu"
                aria-label="Google zemljevid lokacije Guma Bar Zamušani"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}